"use client";

import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import { PriceDetail } from "@repo/apis/browse-search-v2";
import { SavedItem } from "@repo/apis/customers-saveditems-v3";
import { BagApiBadRequestError, isBagApiBadRequestError } from "@repo/bag";
import { Link } from "@repo/next-utils/router";
import * as AsyncAction from "@repo/ui/async-action";
import * as Auth from "@repo/ui/auth";
import { useBag } from "@repo/ui/bag";
import { Box } from "@repo/ui/box";
import { Button } from "@repo/ui/button";
import * as Container from "@repo/ui/container";
import * as Flex from "@repo/ui/flex";
import * as Grid from "@repo/ui/grid";
import { InlineMessage } from "@repo/ui/inline-message";
import * as ProductTile from "@repo/ui/product-tile";
import { ProductVariantSelector } from "@repo/ui/product-variant-select";
import { ProductVariantSheet } from "@repo/ui/product-variant-sheet";
import { SaveForLater } from "@repo/ui/saved-items";
import { useSavedItems } from "@repo/ui/saved-items";
import { useStore } from "@repo/ui/store";
import { ToastProvider, Toast, ToastViewport } from "@repo/ui/toast";
import { prependUrlWithHttps } from "@repo/utils/prepend-url-with-https";
import { removeBrandPrefix } from "@repo/utils/remove-brand-prefix";
import { useWishlistAnalytics } from "@/components/analytics/use-wishlist-analytics";
import { ProductLink } from "@/components/product-link";
import * as Viewport from "@/components/viewport";
import { COLLAGE } from "@/constants";
import { ChevronIcon } from "@/theme/icons/chevron";
import { CloseIcon } from "@/theme/icons/close";
import { InfoRound } from "@/theme/icons/info-round";
import { SpinnerIcon } from "@/theme/icons/spinner";
import { SpinnerLargeIcon } from "@/theme/icons/spinner-large";
import { TickIcon } from "@/theme/icons/tick";
import {
  TYPOGRAPHY_HEADING1,
  TYPOGRAPHY_BODY3_SENTENCE,
  TYPOGRAPHY_LABEL3,
  TYPOGRAPHY_HEADING3,
  TYPOGRAPHY_HEADING3_BOLD,
  TYPOGRAPHY_HEADING4,
  TYPOGRAPHY_BODY3,
} from "@/theme/topshop/typography/typography-constants";
import { getDiscountPercentage } from "@/utils/get-discount-percentage";

import { mapSelectableVariants } from "@/utils/map-selectable-variants";
import { useSmallViewport } from "./../../components/viewport";
import classes from "./index.module.css";

export function WishlistView() {
  const isMobile = useSmallViewport();
  const {
    status: savedItemsStatus,
    savedItems,
    getSavedItems,
  } = useSavedItems();
  const { status: authStatus } = Auth.useAuth();
  const { pushAddToBagEvent } = useWishlistAnalytics(
    savedItems.savedItemProducts,
    savedItemsStatus,
  );
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const {
    countryData: { countryCode, defaultCurrency },
  } = useStore();

  useEffect(() => {
    if (savedItemsStatus === "ready") {
      const loadProducts = async () => {
        setIsLoadingProducts(true);
        try {
          await getSavedItems({ expand: ["variants"] });
        } catch (error) {
          console.error("Error loading saved items products:", error);
        } finally {
          setIsLoadingProducts(false);
        }
      };

      loadProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedItemsStatus]);

  return (
    <ToastProvider>
      <Container.Root size="large" className={classes.container}>
        {savedItemsStatus === "initialising" || isLoadingProducts ? (
          <Flex.Container
            justify="center"
            align="center"
            className={classes.emptyStateContainer}
          >
            <SpinnerLargeIcon
              label="Loading saved items..."
              className={classes.spinnerIcon}
            />
          </Flex.Container>
        ) : (
          <>
            <Box
              paddingY={isMobile ? "md" : "sm"}
              paddingX={isMobile ? "lg" : "3xl"}
            >
              <Flex.Container
                align="center"
                gap={isMobile ? "md" : "sm"}
                justify="start"
              >
                <h1 className={TYPOGRAPHY_HEADING1}>Wishlist</h1>
                <p className={TYPOGRAPHY_BODY3_SENTENCE}>
                  {savedItems.itemCount} items
                </p>
              </Flex.Container>
            </Box>
            <>
              {savedItems.itemCount === 0 ? (
                <Flex.Container
                  align="center"
                  justify="center"
                  className={classes.emptyStateContainer}
                >
                  <Flex.Container
                    column
                    justify="start"
                    gap="md"
                    className={classes.emptyStateInnerContainer}
                  >
                    <h3 className={TYPOGRAPHY_HEADING3}>nothing saved yet</h3>
                    <p className={TYPOGRAPHY_BODY3_SENTENCE}>
                      Want to save your favourite pieces? No problem, just click
                      on the heart icon as you shop and it will appear in your
                      Wishlist.
                    </p>
                    <Button
                      variant="primary"
                      size="medium"
                      alignment="start"
                      asChild
                    >
                      <Link href="/">SHOP NOW</Link>
                    </Button>
                  </Flex.Container>
                </Flex.Container>
              ) : (
                <>
                  {authStatus === "anonymous" ? (
                    <Box paddingY="3xl" paddingX={isMobile ? "xl" : "3xl"}>
                      <Flex.Container align="center" justify="start">
                        <Flex.Container
                          column
                          justify="start"
                          gap="md"
                          className={classes.innerContainer}
                        >
                          <p className={TYPOGRAPHY_BODY3_SENTENCE}>
                            Your Wishlist is only temporary! Sign in or create
                            an account to save them permanently.
                          </p>
                          <Auth.Login
                            variant="primary"
                            size="medium"
                            alignment="start"
                          >
                            SIGN IN
                          </Auth.Login>
                        </Flex.Container>
                      </Flex.Container>
                    </Box>
                  ) : null}
                  <Grid.Container columns={2} rowGap="xl">
                    {savedItems.savedItemProducts.map((item, index) => {
                      const name = removeBrandPrefix({
                        brand: item.brand,
                        text: item.title,
                      });

                      const isFullSize = isMobile && index % 7 === 0;

                      return (
                        <WishlistProduct
                          key={item.id}
                          item={item}
                          country={countryCode}
                          currency={defaultCurrency}
                          isFullSize={isFullSize}
                          title={name}
                          isMobile={isMobile}
                          handleAddToBagEvent={(variantId?: number) =>
                            pushAddToBagEvent(item, variantId)
                          }
                        />
                      );
                    })}
                  </Grid.Container>
                </>
              )}
            </>
          </>
        )}
      </Container.Root>
      <ToastViewport />
    </ToastProvider>
  );
}

export function WishlistProduct({
  item,
  isFullSize = false,
  title,
  isMobile,
  handleAddToBagEvent,
  currency,
  country,
}: {
  item: SavedItem;
  isFullSize?: boolean;
  title: string;
  isMobile: boolean;
  handleAddToBagEvent: (variantId?: number) => void;
  currency: string;
  country: string;
}) {
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [isProductVariantSheetOpen, setIsProductVariantSheetOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { addProduct } = useBag();
  const { removeSavedItems } = useSavedItems();

  const handleAddToBag = useCallback(
    async (itemId: string, variantId?: number) => {
      setErrorMessage(null);
      const productVariantId = variantId || selectedVariantId;

      if (!productVariantId) {
        setErrorMessage(
          "Please select from the available colour and size options.",
        );
        return false;
      }

      setIsLoading(true);

      try {
        await addProduct({ variantId: productVariantId });
        await removeSavedItems({ itemId });
        handleAddToBagEvent(productVariantId);

        return true;
      } catch (error) {
        const message = isBagApiBadRequestError(error)
          ? (error as BagApiBadRequestError).bagErrorMessage
          : "Failed to add to bag.";

        setErrorMessage(message);
        setShowToast(true);

        return false;
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedVariantId],
  );

  return (
    <>
      <Grid.Item key={item.id} span={isFullSize ? 2 : 1}>
        <Flex.Container column gap="sm">
          <ProductTile.Root data-product-id={item.productId}>
            <ProductLink productName={item.title} productId={item.productId}>
              <ProductTile.ProductImage
                imageUrl={prependUrlWithHttps(item.imageUrl)}
                altText={item.title}
                sourceSetProfile={COLLAGE}
                srcSize={isFullSize ? 326 : 163}
              />
            </ProductLink>
            <Box paddingX="md" paddingY="md">
              <Flex.Container
                justify="space-between"
                align="center"
                className={classes.productTileHeader}
              >
                <ProductLink
                  productName={item.title}
                  productId={item.productId}
                >
                  <Flex.Container column>
                    <ProductTile.Title className={TYPOGRAPHY_HEADING4}>
                      {title}
                    </ProductTile.Title>
                    <ProductTile.Price
                      priceData={{
                        currency: item.price.currency,
                        current: item.price.current,
                        previous: item.price.previous,
                        rrp:
                          item.price.rrp.text != null
                            ? (item.price.rrp as PriceDetail)
                            : { value: item.price.rrp.value, text: "" },
                        isMarkedDown: item.price.isMarkedDown,
                        isOutletPrice: item.price.isOutletPrice,
                        discountPercentage: getDiscountPercentage(
                          item.price.current,
                          item.price.previous,
                        ),
                      }}
                      country={country}
                      className={TYPOGRAPHY_BODY3}
                      previousPriceCopy="Previous Price"
                      currentPriceCopy="Current Price"
                      currency={currency}
                    />
                  </Flex.Container>
                </ProductLink>
                <SaveForLater
                  productId={item.productId}
                  colourWayId={item.colourWayId}
                  className={classes.saveForLaterButton}
                />
              </Flex.Container>
            </Box>
          </ProductTile.Root>
          <Box paddingX="md">
            <Viewport.Small>
              <Flex.Container column gap="sm">
                {!isLoading ? (
                  <ProductVariantSheet
                    mappedVariants={mapSelectableVariants(
                      item.variants.map((variant) => ({
                        ...variant,
                        brandSize:
                          variant.displaySizeText || variant.size || "",
                        isInStock: variant.isVariantInStock ?? true,
                        isLowInStock: variant.isVariantLowInStock ?? false,
                      })),
                    )}
                    onSelect={(variantId) =>
                      handleAddToBag(item.id, Number(variantId))
                    }
                    placeholder="Add to bag"
                    header="SELECT SIZE"
                    itemIndicatorContent={<TickIcon />}
                    closeIcon={<CloseIcon label="Close" />}
                    selectItemClassName={TYPOGRAPHY_LABEL3}
                    selectHeaderClassName={TYPOGRAPHY_LABEL3}
                    selectTriggerClassName={TYPOGRAPHY_LABEL3}
                    isOpen={isProductVariantSheetOpen}
                    onOpenChange={setIsProductVariantSheetOpen}
                    disabled={!item.isProductInStock}
                    variant="primary"
                  />
                ) : (
                  <Button variant="secondary" size="small" disabled>
                    <SpinnerIcon label="Adding to bag" />
                  </Button>
                )}
              </Flex.Container>
            </Viewport.Small>
            <Viewport.Large>
              <Flex.Container
                align="center"
                gap="md"
                justify="space-between"
                className={classes.actionsContainer}
              >
                <Flex.Item grow={1}>
                  <ProductVariantSelector
                    disabled={!item.isProductInStock}
                    mappedVariants={mapSelectableVariants(
                      item.variants.map((variant) => ({
                        ...variant,
                        brandSize:
                          variant.displaySizeText || variant.size || "",
                        isInStock: variant.isVariantInStock ?? true,
                        isLowInStock: variant.isVariantLowInStock ?? false,
                      })),
                    )}
                    onSelect={(variantId) => {
                      setSelectedVariantId(Number(variantId));
                    }}
                    placeholder={
                      item.isProductInStock ? "SELECT SIZE" : "OUT OF STOCK"
                    }
                    itemIndicatorContent={<TickIcon />}
                    icon={<ChevronIcon />}
                    selectItemClassName={TYPOGRAPHY_LABEL3}
                    className={TYPOGRAPHY_LABEL3}
                  />
                </Flex.Item>

                <Flex.Item grow={1}>
                  <AsyncAction.Root successTimeoutMs={3000}>
                    <Flex.Container column gap="md">
                      <AsyncAction.Button
                        disabled={!item.isProductInStock}
                        variant="primary"
                        size="large"
                        alignment="start"
                        fullWidth
                        className={clsx(
                          classes.addToBagButton,
                          TYPOGRAPHY_LABEL3,
                        )}
                        onClick={() => handleAddToBag(item.id)}
                      >
                        <AsyncAction.Idle>
                          {item.isProductInStock
                            ? "ADD TO BAG"
                            : "OUT OF STOCK"}
                        </AsyncAction.Idle>
                        <AsyncAction.Error>
                          {item.isProductInStock
                            ? "ADD TO BAG"
                            : "OUT OF STOCK"}
                        </AsyncAction.Error>
                        <AsyncAction.Loading>
                          <SpinnerIcon label="Adding to bag" />
                        </AsyncAction.Loading>
                        <AsyncAction.Success>ADDED TO BAG</AsyncAction.Success>
                      </AsyncAction.Button>
                    </Flex.Container>
                    <Viewport.Large>
                      <AsyncAction.Error>
                        {errorMessage && (
                          <InlineMessage
                            className={classes.inlineMessage}
                            variant="error"
                            icon={<InfoRound />}
                            inlineMessageClass={TYPOGRAPHY_BODY3_SENTENCE}
                          >
                            {errorMessage}
                          </InlineMessage>
                        )}
                      </AsyncAction.Error>
                    </Viewport.Large>
                  </AsyncAction.Root>
                </Flex.Item>
              </Flex.Container>
            </Viewport.Large>
          </Box>
        </Flex.Container>
      </Grid.Item>
      <Toast
        message={errorMessage ?? "Error"}
        toastTitle={TYPOGRAPHY_HEADING3_BOLD}
        toastMessage={TYPOGRAPHY_BODY3_SENTENCE}
        variant="error"
        closeIcon={<CloseIcon />}
        onClose={() => setShowToast(false)}
        open={isMobile && showToast}
      />
    </>
  );
}
